import React, { useRef } from "react";
import PropTypes from "prop-types";
import _ from "underscore";
import moment from "moment";
import Header from "./header";
import Message from "./message";
import MessageTextArea from "./message-text-area";
import { Icon, Layout, PlainButton, Loading } from "@evertrue/et-components";
import { useChatCurrentUser } from "../contexts/chat-current-user-context";
import { parseMessage, getETUserIDFromChatKitUser, isDM } from "../utils/utils";
import ChatActionBar from "./chat-action-bar";
import { useChatNavigationContext } from "../contexts/chat-navigation-context";
import { useChatDependencyContext } from "../contexts/chat-dependency-context";
import MountainSvg from "./mountain-svg";
import { useETCachedUsersByIds, useETCachedUserById } from "../contexts/et-user-context";
import { searchCompare } from "../utils/utils";

// taken from: https://reactjs.org/docs/react-component.html#getsnapshotbeforeupdate
// get the scroll position before 'load more' then restore it after we render more
// use a class and renderProp b/c of getSnapshotBeforeUpdate
class ScrollingList extends React.Component {
  static propTypes = {
    list: PropTypes.array.isRequired,
    children: PropTypes.func.isRequired,
  };

  listRef = React.createRef();
  getSnapshotBeforeUpdate(prevProps, prevState) {
    // Are we adding new items to the list?
    // Capture the scroll position so we can adjust scroll later.
    if (prevProps.list.length < this.props.list.length) {
      const list = this.listRef.current;
      return list.scrollHeight - list.scrollTop;
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // If we have a snapshot value, we've just added new items.
    // Adjust scroll so these new items don't push the old ones out of view.
    // (snapshot here is the value returned from getSnapshotBeforeUpdate)
    if (snapshot !== null) {
      const list = this.listRef.current;
      list.scrollTop = list.scrollHeight - snapshot;
    }
  }

  render() {
    return this.props.children({ ref: this.listRef });
  }
}
const ChatRoom = ({
  messages,
  usersTyping,
  currentRoom,
  loadingRoom,
  roomName,
  areMoreMessagesToFetch,
  fetchOlderMessages,
}) => {
  const { id: roomId } = currentRoom;

  const { currentChatUser, loading } = useChatCurrentUser();
  const [searchText, setSearchText] = React.useState();
  const { oid, app } = useChatDependencyContext();
  const [, dispatchNav] = useChatNavigationContext();

  // single term search
  const filtered_messages = messages.filter(msg => {
    const { text = "" } = parseMessage(msg);
    return searchCompare(text, searchText);
  });

  const messages_by_date = _.groupBy(filtered_messages, message => {
    if (moment(message.createdAt).isSame(moment(), "day")) {
      return "Today";
    } else {
      return moment(message.createdAt).format("MMM Do, YYYY");
    }
  });

  const userTypingEtUserId = usersTyping.length && getETUserIDFromChatKitUser(oid, usersTyping[0].id);
  const { user: firstEtUserTyping } = useETCachedUserById(userTypingEtUserId);

  let typing_indicator = "";
  if (usersTyping.length > 1) {
    typing_indicator = "Many people are typing";
  }
  if (usersTyping.length === 1 && firstEtUserTyping.name) {
    typing_indicator = `${firstEtUserTyping.name} is typing`;
  }

  const room_users = currentRoom.users || [];
  const et_user_ids = room_users.map(user => {
    return getETUserIDFromChatKitUser(oid, user.id);
  });
  useETCachedUsersByIds(et_user_ids);

  // Auto scroll to bottom on mount
  const messagesEndRef = useRef(null);
  const newest_message = _.last(messages) || {};

  React.useEffect(() => {
    if (loadingRoom) return;
    if (newest_message.id) {
      // hack b/c doesn't scroll all the way to end without this
      setTimeout(() => {
        messagesEndRef.current && messagesEndRef.current.scrollIntoView();
      }, 300);
      // when entering page update most recent read to most recent message
    }
    // if newest_message changes (one is added) scroll to bottom
  }, [loadingRoom, newest_message.id]);

  // when getting a new message or leaving a room, save cursor
  React.useEffect(() => {
    const saveCursor = newCursorPosition => {
      if (!currentChatUser.id || !newCursorPosition) return;
      currentChatUser.setReadCursor({
        roomId: roomId,
        position: newCursorPosition,
      });
    };
    if (loadingRoom || !newest_message.id) return;
    saveCursor(newest_message.id);

    // on unmount save cursor to most recent message to account for new ones others
    // typed while you were in the room
    return cleanup => {
      saveCursor(newest_message.id);
    };
  }, [currentChatUser, loadingRoom, newest_message.id, roomId]);

  const is_dm = isDM(currentRoom.name);

  return (
    <div className="chat-room">
      <Layout
        header={
          <div>
            <Header title={roomName} roomId={roomId} showSettings={!(is_dm || app === "vms")} showClose showBack />
            <ChatActionBar placeholder="Search the chatroom" searchText={searchText} setSearchText={setSearchText}>
              {!is_dm && (
                <PlainButton
                  onClick={() => dispatchNav({ type: "MEMBER_LIST", payload: roomId })}
                  className="chat-room--users-count"
                >
                  <Icon icon="person" className="chat-room--users-icon" />
                  {_.size(room_users)}
                </PlainButton>
              )}
            </ChatActionBar>
          </div>
        }
        footerBorder="none"
        footer={
          <div>
            {!!typing_indicator && <div className="chat-room--user-typing">{typing_indicator}</div>}
            {/* don't render this until room comes in from async call. This lets us initialize the saved_text state in MessageTextArea */}
            {currentRoom.id && (
              <MessageTextArea roomName={roomName} sendMessage={currentChatUser.sendMessage} roomId={currentRoom.id} />
            )}
          </div>
        }
      >
        <ScrollingList list={filtered_messages}>
          {({ ref }) => (
            <div className="chat-room--message-list" ref={ref}>
              {loading || loadingRoom ? (
                <Loading />
              ) : _.isEmpty(messages_by_date) ? (
                <div className="chat-room--empty">
                  {searchText ? (
                    <div>No results match your search</div>
                  ) : (
                    <>
                      <MountainSvg width="30%" style={{ opacity: ".7", height: "auto", marginBottom: 12 }} />
                      <div className="chat-room--empty-text">Begin chat</div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {areMoreMessagesToFetch && (
                    <PlainButton onClick={fetchOlderMessages} className="chat-room--load-more">
                      Load more
                    </PlainButton>
                  )}
                  {_.map(messages_by_date, (msgArr, date, idx) => {
                    return (
                      <React.Fragment key={date}>
                        <div className="chat-room--date-wrap">
                          <div className="chat-room--date-wrap-position">
                            <span className="chat-room--date">{date}</span>
                          </div>
                        </div>
                        {msgArr.map(message => {
                          return <Message key={message.id} message={message} searchText={searchText} />;
                        })}
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          )}
        </ScrollingList>
      </Layout>
    </div>
  );
};

ChatRoom.propTypes = {
  currentRoom: PropTypes.object.isRequired,
  areMoreMessagesToFetch: PropTypes.bool.isRequired,
  fetchOlderMessages: PropTypes.func.isRequired,
  loadingRoom: PropTypes.bool,
  messages: PropTypes.array,
  usersTyping: PropTypes.array,
  roomName: PropTypes.string,
};

export default ChatRoom;
